import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BestyData {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  shared_events: number;
}

export interface TopHostData {
  host_id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  events_attended: number;
}

export interface StreakWeek {
  week_start: string;
  week_end: string;
  events: string[];
}

export interface WrappedStats {
  id: string;
  user_id: string;
  year: number;
  total_events: number;
  galleries_visited: number;
  secret_salons_visited: number;
  top_besties: BestyData[];
  top_host: TopHostData | null;
  longest_streak: number;
  streak_details: StreakWeek[];
  monthly_activity: Record<string, number>;
  first_event_date: string | null;
  last_event_date: string | null;
  slide_views?: Record<string, number>;
  share_clicks?: Record<string, number>;
  total_views?: number;
  total_shares?: number;
  last_viewed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export const useWrappedStats = (year: number = 2025) => {
  return useQuery({
    queryKey: ["wrapped-stats", year],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("wrapped_stats")
        .select("*")
        .eq("user_id", user.id)
        .eq("year", year)
        .maybeSingle();

      if (error) throw error;
      
      if (!data) return null;

      // Type cast the JSONB fields
      return {
        ...data,
        top_besties: (data.top_besties || []) as unknown as BestyData[],
        top_host: (data.top_host || null) as unknown as TopHostData | null,
        streak_details: (data.streak_details || []) as unknown as StreakWeek[],
        monthly_activity: (data.monthly_activity || {}) as unknown as Record<string, number>,
      } as WrappedStats;
    },
  });
};
